import React, { useEffect, useRef, useState } from "react";
import i18n from "i18next";
import "./language.css";

// Sprachauswahl für alle innopark24-Portale: Button mit Flagge + Kürzel, Liste mit Landesflaggen.
// Flaggen als Inline-SVG, damit sie auch unter Windows angezeigt werden (dort fehlen Flaggen-Emojis).
const H = ({ colors }) =>
  colors.map((c, i) => <rect key={i} x="0" y={(20 / colors.length) * i} width="30" height={20 / colors.length + 0.01} fill={c} />);
const V = ({ colors }) =>
  colors.map((c, i) => <rect key={i} x={(30 / colors.length) * i} y="0" width={30 / colors.length + 0.01} height="20" fill={c} />);

const FLAGS = {
  de: <H colors={["#000", "#DD0000", "#FFCE00"]} />,
  en: (
    <>
      <rect width="30" height="20" fill="#012169" />
      <path d="M0,0 L30,20 M30,0 L0,20" stroke="#fff" strokeWidth="4" />
      <path d="M0,0 L30,20 M30,0 L0,20" stroke="#C8102E" strokeWidth="1.6" />
      <path d="M15,0 V20 M0,10 H30" stroke="#fff" strokeWidth="6.5" />
      <path d="M15,0 V20 M0,10 H30" stroke="#C8102E" strokeWidth="3.8" />
    </>
  ),
  es: (
    <>
      <rect width="30" height="20" fill="#AA151B" />
      <rect y="5" width="30" height="10" fill="#F1BF00" />
    </>
  ),
  it: <V colors={["#009246", "#fff", "#CE2B37"]} />,
  tr: (
    <>
      <rect width="30" height="20" fill="#E30A17" />
      <circle cx="11" cy="10" r="5" fill="#fff" />
      <circle cx="12.25" cy="10" r="4" fill="#E30A17" />
      <polygon fill="#fff" points="14.50,10.00 15.91,9.50 15.95,8.00 16.86,9.19 18.30,8.77 17.45,10.00 18.30,11.23 16.86,10.81 15.95,12.00 15.91,10.50" />
    </>
  ),
  pl: <H colors={["#fff", "#DC143C"]} />,
  fr: <V colors={["#0055A4", "#fff", "#EF4135"]} />,
  nl: <H colors={["#AE1C28", "#fff", "#21468B"]} />,
};

export const LANGUAGES = [
  { code: "de", name: "Deutsch" },
  { code: "en", name: "English" },
  { code: "es", name: "Español" },
  { code: "it", name: "Italiano" },
  { code: "tr", name: "Türkçe" },
  { code: "pl", name: "Polski" },
  { code: "fr", name: "Français" },
  { code: "nl", name: "Nederlands" },
];

const Flag = ({ code }) => (
  <svg className="ipl-flag" viewBox="0 0 30 20" aria-hidden="true" focusable="false">
    {FLAGS[code]}
  </svg>
);

const current = () => {
  const stored = (localStorage.getItem("_lang") || i18n.language || "de").slice(0, 2);
  return LANGUAGES.some((l) => l.code === stored) ? stored : "de";
};

/** align: "right" (Menü klappt nach links auf) oder "left" */
const LanguageDropdown = ({ align = "right", label = "Sprache / Language" }) => {
  const [lang, setLang] = useState(current);
  const [open, setOpen] = useState(false);
  const root = useRef(null);

  useEffect(() => {
    const sync = (lng) => setLang((lng || "de").slice(0, 2));
    i18n.on("languageChanged", sync);
    return () => i18n.off("languageChanged", sync);
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (root.current && !root.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        const btn = root.current && root.current.querySelector(".ipl-lang-btn");
        if (btn) btn.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem("_lang", code);
    setLang(code);
    setOpen(false);
  };

  return (
    <div className={`ipl-lang-dd ${align === "left" ? "align-left" : ""}`} ref={root}>
      <button
        type="button"
        className="ipl-lang-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((o) => !o)}
      >
        <Flag code={lang} />
        <span>{lang.toUpperCase()}</span>
        <svg className="ipl-chev" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <ul className="ipl-lang-menu" role="listbox" aria-label={label}>
          {LANGUAGES.map((l) => (
            <li key={l.code} role="option" aria-selected={l.code === lang}>
              <button type="button" lang={l.code} onClick={() => choose(l.code)}>
                <Flag code={l.code} />
                <span className="ipl-lang-name">{l.name}</span>
                <span className="ipl-lang-code">{l.code.toUpperCase()}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageDropdown;
